import type { GeneoIotProjectFile } from '@geneo2-web/shared-ui';
import { SessionModeEnum } from '@protos/learning_management/lms.db_pb';
import {
  NavigateFunction,
  URLSearchParamsInit,
  createSearchParams,
} from 'react-router-dom';
import { RESOURCE_PAGE } from '../routeHandling/RoutesNomenclature';
import { ContentApprovalStatusEnum } from '@protos/content_management/content.db_pb';

interface ResourceParams {
  resourceId: string;
  lessonId?: string;
  lessonSessionId?: number;
  subjectId?: number;
  chapterId?: number;
  topicId?: number;
  resourceIndex?: number;
  classId?: number;
  section?: string;
  connectedClass?: string;
  connectedClassResourceSessionId?: string;
  sectionId?: bigint;
  approvalStatus?: ContentApprovalStatusEnum;
  /** Project assessments only: the project the resource is being opened from. */
  assessmentId?: number | bigint;
  practicalReview?: { project?: GeneoIotProjectFile };
  /** Opened on its own from a resource list, outside any sequence to page through. */
  standalone?: boolean;
}

export type ResourceViewMode = 'add' | 'replace' | 'view' | undefined;

const resourceSearchParams = (
  resourceParams: ResourceParams,
  sessionMode: SessionModeEnum,
  viewMode?: ResourceViewMode
): URLSearchParamsInit => {
  const {
    resourceId,
    lessonId,
    lessonSessionId,
    subjectId,
    chapterId,
    topicId,
    resourceIndex,
    classId,
    section,
    connectedClass,
    connectedClassResourceSessionId,
    sectionId,
    assessmentId,
    standalone,
  } = resourceParams;
  const params: URLSearchParamsInit = {};
  if (connectedClass) {
    params.connectedClass = connectedClass;
  }
  if (resourceId) {
    params.resourceId = resourceId;
  }
  if (lessonId) {
    params.lessonId = lessonId;
  }
  if (typeof lessonSessionId == 'number') {
    params.lessonSessionId = lessonSessionId.toString();
  }
  if (typeof subjectId == 'number') {
    params.subjectId = subjectId.toString();
  }
  if (typeof chapterId == 'number') {
    params.chapterId = chapterId.toString();
  }
  if (typeof topicId == 'number') {
    params.topicId = topicId.toString();
  }
  if (typeof classId == 'number') {
    params.classId = classId.toString();
  }
  if (typeof sectionId == 'bigint') {
    params.sectionId = sectionId.toString();
  }
  if (assessmentId !== undefined) {
    params.assessmentId = assessmentId.toString();
  }
  if (viewMode) {
    params.viewMode = viewMode;
  }
  if (sessionMode) {
    params.sessionMode = sessionMode.toString();
  }
  if (section) {
    params.section = section;
  }
  if (typeof resourceIndex == 'number') {
    params.resourceIndex = resourceIndex.toString();
  }
  if (connectedClassResourceSessionId) {
    params.connectedClassResourceSessionId =
      connectedClassResourceSessionId.toString();
  }
  if (standalone) {
    params.standalone = 'true';
  }
  return params;
};

export const onResourceClick = (
  navigate: NavigateFunction,
  resourceParams: ResourceParams,
  sessionMode: SessionModeEnum,
  viewMode?: ResourceViewMode,
  /** Swap the current history entry rather than stacking a new one. */
  replace?: boolean
) => {
  const { resourceId, approvalStatus, practicalReview } = resourceParams;
  const params = resourceSearchParams(resourceParams, sessionMode, viewMode);
  navigate(
    {
      pathname: RESOURCE_PAGE,
      search: resourceId ? `?${createSearchParams(params)}` : undefined,
    },
    {
      replace,
      state: {
        approvalStatus: approvalStatus ? approvalStatus : undefined, // Pass state separately here
        practicalReview,
      },
    }
  );
};

/**
 * The same destination `onResourceClick` routes to, as an absolute URL - for
 * links that open the resource in its own tab rather than taking over the
 * current one. The router state `onResourceClick` carries cannot cross a tab
 * boundary, so this is only for resources that need none of it.
 */
export const resourceUrl = (
  resourceParams: ResourceParams,
  sessionMode: SessionModeEnum,
  viewMode?: ResourceViewMode
) => {
  const params = resourceSearchParams(resourceParams, sessionMode, viewMode);
  return `${window.location.origin}${RESOURCE_PAGE}?${createSearchParams(
    params
  )}`;
};
